import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Icon from '../components/AppIcon';

const ToastContext = createContext({});

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};

const ICONS = {
  success: 'CheckCircle',
  error: 'XCircle',
  warning: 'AlertTriangle',
  info: 'Info',
};

const COLORS = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
};

const ICON_COLORS = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-blue-500',
};

const ToastItem = ({ toast, onRemove }) => (
  <div
    className={`flex items-start gap-12 px-16 py-14 rounded-xl border shadow-lg text-sm font-medium max-w-sm w-full ${COLORS?.[toast?.type]}`}
  >
    <Icon name={ICONS?.[toast?.type]} size={18} className={`flex-shrink-0 mt-0.5 ${ICON_COLORS?.[toast?.type]}`} />
    <div className="flex-1 min-w-0">
      {toast?.title && <div className="font-semibold mb-2">{toast?.title}</div>}
      <div className="leading-snug opacity-90">{toast?.message}</div>
    </div>
    <button
      onClick={() => onRemove(toast?.id)}
      className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity ml-4"
    >
      <Icon name="X" size={14} />
    </button>
  </div>
);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev?.filter(t => t?.id !== id));
  }, []);

  const addToast = useCallback(({ type = 'info', title, message, duration = 4000 }) => {
    const id = ++counterRef.current;
    setToasts(prev => [...prev, { id, type, title, message }]);
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, [removeToast]);

  const toast = {
    success: (message, title) => addToast({ type: 'success', message, title }),
    error: (message, title) => addToast({ type: 'error', message, title, duration: 6000 }),
    warning: (message, title) => addToast({ type: 'warning', message, title }),
    info: (message, title) => addToast({ type: 'info', message, title }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed bottom-24 right-24 z-[9999] flex flex-col gap-10 pointer-events-none">
        {toasts?.map(t => (
          <div key={t?.id} className="pointer-events-auto">
            <ToastItem toast={t} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastContext;
